import React, { useState, useEffect, useCallback } from "react";
import {
  FaEnvelope,
  FaSearch,
  FaSyncAlt,
  FaRedo,
  FaExclamationCircle,
  FaCheck,
  FaCheckCircle
} from "react-icons/fa";
import { fetchWithAuth } from "../../../utils/api";
import "./TopmateBookings.css";

function TopmateEmailLogs() {
  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [resendingId, setResendingId] = useState(null);

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [type, setType] = useState("all");
  const [dateRange, setDateRange] = useState("all");
  const [page, setPage] = useState(1);

  const fetchEmailLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const params = new URLSearchParams({
        page: String(page),
        limit: "10",
        search: search.trim(),
        status,
        type,
        dateRange
      });

      const res = await fetchWithAuth(`/admin/topmate/email-logs?${params.toString()}`);
      const data = await res.json();

      if (data.success) {
        setLogs(data.logs || []);
        setPagination(data.pagination || { total: 0, page: 1, limit: 10, totalPages: 1 });
      } else {
        setError(data.message || "Failed to load email logs");
      }
    } catch (err) {
      setError("Network error fetching email logs");
    } finally {
      setLoading(false);
    }
  }, [page, search, status, type, dateRange]);

  useEffect(() => {
    fetchEmailLogs();
  }, [fetchEmailLogs]);

  const handleResend = async (logId) => {
    try {
      setResendingId(logId);
      setError("");

      const res = await fetchWithAuth(`/admin/topmate/email-logs/${logId}/resend`, {
        method: "POST"
      });
      const data = await res.json();

      if (data.success) {
        setSuccessMessage(data.message || "Email re-sent successfully!");
        setTimeout(() => setSuccessMessage(""), 5000);
        fetchEmailLogs();
      } else {
        setError(data.message || "Failed to re-send email");
      }
    } catch (err) {
      setError("Network error attempting to resend email");
    } finally {
      setResendingId(null);
    }
  };

  return (
    <div className="topmate-dashboard-container">
      {/* HEADER */}
      <div className="topmate-header-card">
        <div className="topmate-header-text">
          <div className="topmate-header-badge">
            <FaEnvelope /> Email Dispatch System
          </div>
          <h1>Email Logs</h1>
          <p>
            Authoritative audit trail of all customer confirmation emails dispatched via Brevo SMTP API with delivery
            statuses and retry capabilities.
          </p>
        </div>

        <div className="topmate-header-actions">
          <button className="topmate-btn-secondary" onClick={fetchEmailLogs} disabled={loading}>
            <FaSyncAlt className={loading ? "spin-icon" : ""} /> Refresh Logs
          </button>
        </div>
      </div>

      {error && (
        <div className="topmate-alert danger">
          <FaExclamationCircle /> <span>{error}</span>
        </div>
      )}

      {successMessage && (
        <div className="topmate-alert success">
          <FaCheck /> <span>{successMessage}</span>
        </div>
      )}

      {/* FILTERS */}
      <div className="filter-controls-bar">
        <div className="search-input-wrap">
          <FaSearch className="search-icon" />
          <input
            type="text"
            className="topmate-input"
            placeholder="Search recipient, email, subject, IDs..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>

        <select
          className="topmate-select"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
        >
          <option value="all">All Delivery Statuses</option>
          <option value="Sent">Sent (Success)</option>
          <option value="Failed">Failed</option>
          <option value="Retrying">Retrying</option>
          <option value="Sending">Sending</option>
          <option value="Pending">Pending</option>
        </select>

        <select
          className="topmate-select"
          value={type}
          onChange={(e) => {
            setType(e.target.value);
            setPage(1);
          }}
        >
          <option value="all">All Email Types</option>
          <option value="Session Confirmation">Session Confirmation</option>
          <option value="Digital Purchase Confirmation">Digital Purchase Confirmation</option>
        </select>

        <select
          className="topmate-select"
          value={dateRange}
          onChange={(e) => {
            setDateRange(e.target.value);
            setPage(1);
          }}
        >
          <option value="all">All Time (IST)</option>
          <option value="today">Today (IST)</option>
          <option value="7d">Last 7 Days (IST)</option>
          <option value="30d">Last 30 Days (IST)</option>
          <option value="90d">Last 90 Days (IST)</option>
          <option value="year">This Year (IST)</option>
        </select>
      </div>

      {/* TABLE */}
      <div className="topmate-card">
        {loading ? (
          <div className="topmate-state-box">
            <div className="topmate-spinner" />
            <p>Loading email logs...</p>
          </div>
        ) : logs.length === 0 ? (
          <div className="topmate-state-box">
            <p>No email logs found matching the filter criteria.</p>
          </div>
        ) : (
          <>
            <div className="topmate-table-wrapper">
              <table className="topmate-table">
                <thead>
                  <tr>
                    <th>Timestamp (IST)</th>
                    <th>Recipient</th>
                    <th>Subject</th>
                    <th>Email Type</th>
                    <th>Topmate ID</th>
                    <th>Portfolio Booking ID</th>
                    <th>Status</th>
                    <th>Attempts</th>
                    <th>Provider Message ID</th>
                    <th>Failure Reason</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr key={log._id}>
                      <td style={{ color: "#38bdf8" }}>{log.sentAtIST}</td>
                      <td>
                        <div>
                          <strong>{log.recipientName || "Customer"}</strong>
                          <div style={{ fontSize: "0.75rem", color: "#94a3b8" }}>{log.recipientEmail}</div>
                        </div>
                      </td>
                      <td style={{ maxWidth: "220px", overflow: "hidden", textOverflow: "ellipsis" }}>
                        <span title={log.subject}>{log.subject}</span>
                      </td>
                      <td>
                        <span style={{ fontSize: "0.75rem", background: "rgba(255,255,255,0.06)", padding: "2px 8px", borderRadius: "6px" }}>
                          {log.emailType}
                        </span>
                      </td>
                      <td>
                        <code style={{ fontSize: "0.75rem", background: "rgba(255,255,255,0.06)", padding: "2px 6px", borderRadius: "4px" }}>
                          {log.topmateId || "N/A"}
                        </code>
                      </td>
                      <td>
                        {log.portfolioBookingId ? (
                          <code style={{ fontSize: "0.75rem", background: "rgba(56,189,248,0.1)", color: "#38bdf8", padding: "2px 6px", borderRadius: "4px" }}>
                            {log.portfolioBookingId}
                          </code>
                        ) : (
                          <span style={{ color: "#64748b" }}>—</span>
                        )}
                      </td>
                      <td>
                        <span className={`status-pill ${log.status?.toLowerCase()}`}>
                          {log.status === "Sent" && <FaCheckCircle style={{ fontSize: "0.7rem" }} />}
                          {log.status}
                        </span>
                      </td>
                      <td>
                        <span style={{ fontSize: "0.75rem", color: "#94a3b8" }}>
                          {log.attemptCount || 1} {log.retryStatus && log.retryStatus !== "None" ? `(${log.retryStatus})` : ""}
                        </span>
                      </td>
                      <td>
                        <code style={{ fontSize: "0.72rem", color: "#94a3b8" }}>
                          {log.providerMessageId || "—"}
                        </code>
                      </td>
                      <td>
                        {log.failureReason ? (
                          <span style={{ color: "#f87171", fontSize: "0.75rem" }} title={log.failureReason}>
                            {log.failureReason.length > 25 ? `${log.failureReason.substring(0, 25)}...` : log.failureReason}
                          </span>
                        ) : (
                          <span style={{ color: "#64748b" }}>—</span>
                        )}
                      </td>
                      <td>
                        <button
                          className="btn-action"
                          onClick={() => handleResend(log._id)}
                          disabled={resendingId === log._id}
                          title="Resend this email"
                        >
                          <FaRedo className={resendingId === log._id ? "spin-icon" : ""} />
                          {resendingId === log._id ? "Resending..." : "Resend"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="topmate-pagination">
              <div>
                Showing {(pagination.page - 1) * pagination.limit + 1} -{" "}
                {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} logs
              </div>
              <div className="pagination-buttons">
                <button
                  className="btn-page"
                  disabled={pagination.page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </button>
                <span style={{ fontSize: "0.8rem", color: "#f8fafc" }}>
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <button
                  className="btn-page"
                  disabled={pagination.page >= pagination.totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default TopmateEmailLogs;
